import { ChevronRight, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  useFeeds,
  usePlaylistPages,
  useSchedules,
  useSlotsForSchedules,
} from "../../api/hooks.js";
import { Button } from "../../design-system/button.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { PageHeading } from "../shared/page-heading.js";
import { PlaylistDialog } from "./playlist-dialog.js";
import { playlistUsageIndex } from "./usage.js";
import { UsedBy } from "./used-by.js";
import type { Playlist } from "../../api/types.js";

/**
 * Every playlist, as global objects.
 *
 * Nothing on this screen is scoped to a feed, and the "used by" column runs the only
 * direction the reference actually runs: feeds name playlists, so which feeds use a
 * playlist is read off the feeds. One playlist driving three feeds is a row with
 * three names on it and nothing else.
 */
export function PlaylistsPage() {
  const playlists = usePlaylistPages();
  const feeds = useFeeds(true);
  const schedules = useSchedules(true);

  // Only schedules some feed is actually assigned can put a playlist on the air, so
  // those are the ones whose slots are worth reading.
  const assignedScheduleIds = useMemo(
    () =>
      [
        ...new Set(
          (feeds.data ?? [])
            .map((feed) => feed.scheduleId)
            .filter((scheduleId) => scheduleId !== null),
        ),
      ].filter((scheduleId) =>
        (schedules.data ?? []).some((schedule) => schedule.id === scheduleId),
      ),
    [feeds.data, schedules.data],
  );
  const slots = useSlotsForSchedules(assignedScheduleIds);

  const usage = useMemo(
    () =>
      playlistUsageIndex(
        playlists.items.map((playlist) => playlist.id),
        feeds.data ?? [],
        slots,
      ),
    [feeds.data, playlists.items, slots],
  );

  const [creating, setCreating] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeading
          subtitle="Sets of tracks your feeds play from."
          title="Playlists"
        />
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          New playlist
        </Button>
      </div>

      {playlists.loading ? (
        <StateSurface kind="loading" title="Reading every playlist" />
      ) : playlists.error ? (
        <StateSurface
          action={
            <Button onClick={playlists.reload} size="small" variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title="The playlists could not be read"
        >
          {playlists.error}
        </StateSurface>
      ) : playlists.items.length === 0 ? (
        <StateSurface
          action={
            <Button
              onClick={() => {
                setCreating(true);
              }}
              size="small"
            >
              <Plus aria-hidden="true" />
              New playlist
            </Button>
          }
          kind="empty"
          title="No playlists yet"
        >
          A playlist chooses from the library. A feed needs one before it can
          play anything.
        </StateSurface>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {playlists.items.map((playlist) => (
              <li key={playlist.id}>
                <PlaylistRow
                  playlist={playlist}
                  uses={usage[playlist.id] ?? []}
                />
              </li>
            ))}
          </ul>
          {playlists.hasMore ? (
            <div className="flex justify-center pb-2">
              <Button
                disabled={playlists.loadingMore}
                onClick={playlists.loadMore}
                size="small"
                variant="secondary"
              >
                {playlists.loadingMore ? "Reading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <PlaylistDialog
        onClose={() => {
          setCreating(false);
        }}
        onSaved={playlists.reload}
        open={creating}
      />
    </div>
  );
}

const MODE_LABELS: Record<string, string> = {
  ordered: "Ordered",
  shuffle: "Shuffle",
  weighted: "Weighted",
};

function PlaylistRow({
  playlist,
  uses,
}: {
  playlist: Playlist;
  uses: ReturnType<typeof playlistUsageIndex>[string];
}) {
  const separation = [
    playlist.minTrackSeparationMinutes > 0
      ? `${String(playlist.minTrackSeparationMinutes)} min between plays`
      : null,
    playlist.minArtistSeparationMinutes > 0
      ? `${String(playlist.minArtistSeparationMinutes)} min between artists`
      : null,
  ].filter((part) => part !== null);

  return (
    <Link
      className="flex items-center gap-4 rounded-md border border-border bg-surface px-4 py-3.5 transition-colors hover:border-accent-line"
      to={`/playlists/${encodeURIComponent(playlist.id)}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="truncate text-sm font-semibold text-ink">
            {playlist.name}
          </h2>
          <span className="rounded-full bg-surface-hi px-2 py-0.5 font-mono text-[0.5625rem] tracking-[0.08em] text-ink-3 uppercase">
            {MODE_LABELS[playlist.mode] ?? playlist.mode}
          </span>
          {playlist.enabled ? null : (
            <span className="rounded-full border border-border-hi px-2 py-0.5 font-mono text-[0.5625rem] tracking-[0.08em] text-ink-3 uppercase">
              Disabled
            </span>
          )}
        </div>
        {separation.length > 0 ? (
          <p className="text-xs text-ink-3">{separation.join(" · ")}</p>
        ) : null}
        <UsedBy showRoles uses={uses} />
      </div>
      <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
    </Link>
  );
}
